import { create } from 'zustand';
import { ulid } from 'ulid';
import { type BatchProgress } from '@/types/batch';
import { TaskState } from '@/types/uploads';

type GetProgressFn = (batchId: string) => Promise<BatchProgress>;

interface BatchTask {
  id: string;
  label: string;
  state: TaskState;
  progress: number;
  totalJobs: number;
  failedJobs: number;
}

interface BatchStore {
  batches: Array<BatchTask>;
  totalProgress: number;
  pollProgress: (batchId: string, getProgress: GetProgressFn, label: string) => Promise<void>;
  markBatchErrored: (label: string) => void;
  removeBatch: (batchId: string) => void;
}

export const useBatchStore = create<BatchStore>((set, get) => {
  const calculateTotalProgress = (batches: Array<BatchTask>): number => {
    if (batches.length === 0) {
      return 0;
    }
    const sum = batches.reduce((acc, batch) => acc + batch.progress, 0);
    return sum / batches.length;
  };

  // 30-minute safety cap on any single batch poll. If the backend never marks the
  // batch finished (stuck worker, dropped `finished_at` write) we give up rather
  // than hammer the API forever. A refresh will pick up whatever state landed.
  const MAX_POLL_ATTEMPTS = 60 * 30;

  const pollProgress = async (batchId: string, getProgress: GetProgressFn, label: string): Promise<void> => {
    if (!get().batches.find((batch) => batch.id === batchId)) {
      set((prev) => ({
        batches: [
          ...prev.batches,
          { id: batchId, label, state: TaskState.InProgress, progress: 0, totalJobs: 0, failedJobs: 0 },
        ],
      }));
    }

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      // Stop if the user dismissed the batch between iterations - otherwise the
      // loop would silently keep hitting the backend and re-add-then-no-op forever.
      if (attempt > 0 && !get().batches.find((batch) => batch.id === batchId)) {
        return;
      }

      let response: BatchProgress;
      try {
        response = await getProgress(batchId);
      } catch (err) {
        set((prev) => ({
          batches: prev.batches.map((batch) => (batch.id === batchId ? { ...batch, state: TaskState.Errored } : batch)),
        }));
        throw err;
      }

      set((prev) => {
        const updatedBatches = prev.batches.map((batch) =>
          batch.id === batchId
            ? {
                ...batch,
                progress: response.progress,
                totalJobs: response.totalJobs,
                failedJobs: response.failedJobs,
              }
            : batch,
        );
        return {
          batches: updatedBatches,
          totalProgress: calculateTotalProgress(updatedBatches),
        };
      });

      if (response.finished) {
        const nextState = response.hasFailures ? TaskState.Errored : TaskState.Finished;
        set((prev) => ({
          batches: prev.batches.map((batch) => (batch.id === batchId ? { ...batch, state: nextState } : batch)),
        }));
        return;
      }

      await new Promise((res) => setTimeout(res, 1000));
    }

    set((prev) => ({
      batches: prev.batches.map((batch) => (batch.id === batchId ? { ...batch, state: TaskState.Errored } : batch)),
    }));
  };

  const markBatchErrored = (label: string) => {
    const erroredId = `errored-${ulid()}`;
    set((prev) => {
      const updatedBatches = [
        ...prev.batches,
        {
          id: erroredId,
          label,
          state: TaskState.Errored,
          progress: 100,
          totalJobs: 0,
          failedJobs: 0,
        },
      ];
      return {
        batches: updatedBatches,
        totalProgress: calculateTotalProgress(updatedBatches),
      };
    });
  };

  const removeBatch = (batchId: string) => {
    set((prev) => {
      const updatedBatches = prev.batches.filter((batch) => batch.id !== batchId);
      return {
        batches: updatedBatches,
        totalProgress: calculateTotalProgress(updatedBatches),
      };
    });
  };

  return {
    batches: [],
    totalProgress: 0,
    pollProgress,
    markBatchErrored,
    removeBatch,
  };
});
